import React, { forwardRef } from 'react';
import { ShieldCheck, AlertTriangle } from 'lucide-react';
import { renderWithRedactions } from "@/components/TextFormatter";
import { API, BACKEND_URL } from "@/lib/api";
import { alignedTimelineFacts, displayTitleForTopic } from "@/lib/factAlignment";

export const SocialShareCard = forwardRef(({ topic }, ref) => {
    if (!topic) return null;
    const displayTitle = displayTitleForTopic(topic);

    // Use the first verified fact as the core truth for the card
    const hasTimeline = topic.verified_facts && topic.verified_facts.length > 0 && typeof topic.verified_facts[0] === 'object';
    
    let claim = "";
    let truth = "";

    if (hasTimeline) {
        const orderedFacts = alignedTimelineFacts(topic.verified_facts, topic);
        claim = orderedFacts[0]?.media_claim || "";
        truth = orderedFacts[0]?.truth || "";
    } else {
        // Fallback for legacy topics
        claim = (topic.media_narratives && topic.media_narratives[0]) || "A versão oficial partilhada pela media.";
        truth = (topic.verified_facts && topic.verified_facts[0]) || "A verdade que tentaram ocultar.";
    }

    return (
        <div
            aria-hidden="true"
            inert=""
            role="presentation"
            style={{
                position: "fixed",
                top: 0,
                left: "-2200px",
                width: "1080px",
                height: "1080px",
                overflow: "hidden",
                pointerEvents: "none",
                zIndex: -1,
            }}
        >
            <div
                ref={ref}
                className="relative bg-[#0c0c0c] text-white flex flex-col justify-between"
                style={{ 
                    width: '1080px', 
                    height: '1080px', 
                    padding: '80px',
                    fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif'
                }}
            >
                <div className="absolute inset-0 crosshair-bg opacity-30 pointer-events-none" />
                
                {/* Header */}
                <div className="relative z-10 flex justify-between items-start w-full border-b-2 border-white/20 pb-6">
                    <div>
                        <h2 className="text-3xl font-bold tracking-[0.2em] uppercase text-[#ff3333]">
                            Dossiê Desclassificado
                        </h2>
                        <p className="text-xl font-mono text-white/50 mt-2">
                            DOSSIÊ #{String(topic.id).slice(0, 8)} | CAT: {topic.category?.toUpperCase() || 'CLASSIFICADO'}
                        </p>
                    </div>
                <div className="border-4 border-[#ff3333] text-[#ff3333] px-4 py-2 font-bold tracking-widest uppercase transform rotate-[-5deg]">
                    Confidencial
                </div>
            </div>

            {/* Middle Content */}
            <div className="flex-1 flex flex-col justify-center py-6 gap-8">
                {/* Title */}
                <div className="relative z-10">
                    <h1 
                        className={`font-serif font-bold leading-tight text-white ${
                            displayTitle.length > 110 ? 'text-4xl' : 
                            displayTitle.length > 70 ? 'text-5xl' : 'text-6xl'
                        }`} 
                        style={{ textShadow: '2px 2px 0px rgba(255, 51, 51, 0.3)'}}
                    >
                        {displayTitle}
                    </h1>
                </div>

                {/* Facts Vs Narrative Section with Image */}
                <div className="relative z-10 flex items-center gap-8 w-full">
                    
                    <div className={`flex flex-col gap-4 ${topic.image_url ? 'w-[75%]' : 'w-full'}`}>
                        {/* Media Narrative */}
                        <div className="bg-red-950/30 border-l-4 border-[#ff3333] p-4 sm:p-5">
                            <div className="flex items-center gap-3 mb-2">
                                <AlertTriangle size={20} color="#ff3333" />
                                <span className="text-lg font-bold uppercase tracking-wider text-[#ff3333]">
                                    A Mentira da Media
                                </span>
                            </div>
                            <p className={`text-white/90 leading-snug font-serif ${
                                claim.length > 150 ? 'text-xl' : claim.length > 90 ? 'text-2xl' : 'text-3xl'
                            } overflow-hidden`} style={{ display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical' }}>
                                "{renderWithRedactions(claim, topic.sources || [])}"
                            </p>
                        </div>

                        {/* Verified Truth */}
                        <div className="bg-green-950/30 border-l-4 border-[#33ffaa] p-4 sm:p-5">
                            <div className="flex items-center gap-3 mb-2">
                                <ShieldCheck size={20} color="#33ffaa" />
                                <span className="text-lg font-bold uppercase tracking-wider text-[#33ffaa]">
                                    A Verdade Documentada
                                </span>
                            </div>
                            <p className={`text-white leading-snug font-serif ${
                                truth.length > 150 ? 'text-xl' : truth.length > 90 ? 'text-2xl' : 'text-3xl'
                            } overflow-hidden`} style={{ display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical' }}>
                                {renderWithRedactions(truth, topic.sources || [])}
                            </p>
                        </div>
                    </div>

                    {/* Polaroid Attachment (if image exists) */}
                    {topic.image_url && (
                        <div className="w-[25%] flex justify-center items-center">
                            <div 
                                className="transform rotate-6 drop-shadow-2xl bg-white p-4 pb-12 w-64 h-64 relative"
                                style={{
                                    boxShadow: '0 20px 40px rgba(0,0,0,0.8), inset 0 0 10px rgba(0,0,0,0.1)'
                                }}
                            >
                                <div className="w-full h-full relative overflow-hidden bg-gray-200">
                                    <img 
                                        src={topic.image_url.startsWith('/uploads/') ? `${BACKEND_URL}${topic.image_url}` : `${API}/proxy-image?url=${encodeURIComponent(topic.image_url)}`} 
                                        alt="Dossiê Visual" 
                                        className="w-full h-full object-cover grayscale contrast-125"
                                        crossOrigin="anonymous"
                                    />
                                </div>
                                <div className="absolute bottom-3 left-4 text-[#ff3333] font-mono text-sm tracking-widest font-bold uppercase">
                                    ANEXO #{String(topic.id).slice(0, 4)}
                                </div>
                                {/* Tape effect */}
                                <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-24 h-8 bg-white/40 backdrop-blur-sm transform -rotate-2" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                            </div>
                        </div>
                    )}

                </div>
            </div>

            {/* Footer */}
            <div className="relative z-10 pt-6 border-t border-white/20 flex justify-between items-end w-full">
                <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center">
                        <span className="text-black font-bold text-3xl">V</span>
                    </div>
                    <div className="flex flex-col">
                        <span className="text-3xl font-bold tracking-widest text-white">VERDADE OCULTA</span>
                        <span className="text-xl font-mono text-white/50">www.verdadeoculta.pt</span>
                    </div>
                </div>
                
                {topic.controversy_score > 70 && (
                    <div className="text-right">
                        <div className="text-xl font-mono text-[#ff3333]">ÍNDICE DE OCULTAÇÃO</div>
                        <div className="text-6xl font-bold text-[#ff3333]">{topic.controversy_score}%</div>
                    </div>
                )}
            </div>
        </div>
        </div>
    );
});

SocialShareCard.displayName = 'SocialShareCard';
